import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchCarrierCodes } from '../data-provider';
import type { CarrierCode, CarrierCodeFilters, CarrierCodes } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

export const carrierCodesQueryOptions = (
  filters: Partial<CarrierCodeFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const carrierCodes = await fetchCarrierCodes(filters, { signal });
      put(carrierCodes.list);
      return carrierCodes;
    },
    queryKey: CACHE_KEYS.carrierCodes(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_CARRIER_CODES_CACHE_KEY = btoa('internal-carrier-codes-cache-key');

const INTERNAL_QUERY_CARRIER_CODES_KEY = carrierCodesQueryOptions({
  codeDesc: INTERNAL_CARRIER_CODES_CACHE_KEY,
}).queryKey;

const getCarrierCodeFallback = (carrierCodeId: string): CarrierCode => {
  logger.warn(`Carrier code "${carrierCodeId}" was not found. Verify the data model.`);
  return {
    carrierCodeId,
    carrierKey: 0,
    codeDesc: carrierCodeId,
    codeType: 'PACKCODE',
    id: Number(carrierCodeId) || 0,
    label: carrierCodeId,
  };
};

export const put = async (carrierCodes: CarrierCode[]) => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CARRIER_CODES_KEY);

  const newCarrierCodes = carrierCodes.reduce(
    (acc, carrierCode) => {
      acc[String(carrierCode.id)] = carrierCode;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_CARRIER_CODES_KEY, {
    list: Object.values(newCarrierCodes),
    map: newCarrierCodes,
  });
};

export const getAll = (): CarrierCodes | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_CARRIER_CODES_KEY);
};

export const find = async (carrierCodeId: string): Promise<CarrierCode> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CARRIER_CODES_KEY);

  const carrierCode = data?.map[carrierCodeId];
  if (carrierCode) {
    return carrierCode;
  }

  try {
    const newData = await queryClient.query(carrierCodesQueryOptions({ search: { id: carrierCodeId } }));
    if (newData.map[carrierCodeId]) {
      void put(newData.list);
      return newData.map[carrierCodeId];
    }
  } catch {
    return getCarrierCodeFallback(carrierCodeId);
  }

  return getCarrierCodeFallback(carrierCodeId);
};

export const findAll = async (carrierCodeIds: string[]): Promise<CarrierCode[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CARRIER_CODES_KEY);

  const missingIds: string[] = [];
  const carrierCodes: CarrierCode[] = [];

  for (const carrierCodeId of carrierCodeIds) {
    const carrierCode = data?.map[carrierCodeId];
    if (carrierCode) {
      carrierCodes.push(carrierCode);
    } else {
      missingIds.push(carrierCodeId);
    }
  }

  if (!missingIds.length) {
    return carrierCodes;
  }

  try {
    const newData = await queryClient.query(carrierCodesQueryOptions({ search: { id: missingIds.join(',') } }));
    void put(newData.list);
    for (const missingId of missingIds) {
      const carrierCode = newData.map[missingId];
      if (carrierCode) {
        carrierCodes.push(carrierCode);
      } else {
        carrierCodes.push(getCarrierCodeFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      carrierCodes.push(getCarrierCodeFallback(missingId));
    }
  }

  return carrierCodes;
};
